import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronDown, Mail, Phone } from 'lucide-react';

const SECTIONS = [
  {
    id: 'conveyancing',
    title: 'Residential Conveyancing',
    lead: 'Our fees for the conveyance of residential real property owned by individuals.',
    body: (
      <>
        <h3>Freehold or Leasehold Sales or Purchases &mdash; Our Fees</h3>
        <ul>
          <li><strong>Freehold sale</strong> up to &pound;1.5m: typically <strong>&pound;1,500 &ndash; &pound;2,500</strong> plus VAT and disbursements.</li>
          <li><strong>Freehold purchase</strong> up to &pound;1.5m (other than new build): typically <strong>&pound;1,750 &ndash; &pound;2,750</strong> plus VAT and disbursements.</li>
          <li><strong>Leasehold sale</strong> up to &pound;1.5m: typically <strong>&pound;2,250 &ndash; &pound;3,250</strong> plus VAT and disbursements.</li>
          <li><strong>Leasehold purchase</strong> up to &pound;1.5m (other than new build): typically <strong>&pound;2,500 &ndash; &pound;3,000</strong> plus VAT and disbursements.</li>
        </ul>
        <p><strong>New build:</strong> for a property under or recently under construction where you are the first occupier, the above ranges may be increased by a reasonable amount to reflect the additional work involved.</p>
        <p><strong>Joint purchases:</strong> two or more individuals buying together may need to enter into a co-ownership agreement. We will propose a reasonable fee for the additional documentation.</p>

        <h3>Disbursements</h3>
        <p>Disbursements are third-party costs necessary to progress your matter.</p>
        <ul>
          <li><strong>Sale:</strong> Land Registry Official Copies at &pound;3 per document (budget c. &pound;21). Leasehold sales may attract a landlord/managing agent legal pack fee.</li>
          <li><strong>Search fees</strong> (Local Authority, Drainage, Chancel Check, Environmental, Ground Stability): budget up to c. <strong>&pound;500</strong>.</li>
          <li><strong>HM Land Registry fees:</strong> &pound;20 minimum to &pound;500 maximum (Land Registry Portal discounted).</li>
          <li><strong>Stamp Duty Land Tax</strong> (or Welsh Land Transaction Tax): calculated on purchase price via the <a href="https://www.tax.service.gov.uk/calculate-stamp-duty-land-tax/#/intro" target="_blank" rel="noopener noreferrer">HMRC calculator</a>.</li>
          <li><strong>Money laundering checks:</strong> document production fee &pound;8 + VAT, plus verification fee (&pound;6 &ndash; &pound;12 + VAT per person depending on circumstances). Overseas or non-UK-address individuals from &pound;40 + VAT.</li>
          <li><strong>Money transfer fee:</strong> &pound;35 + VAT per telegraphic transfer.</li>
        </ul>

        <h3>Factors that may affect fees or timing</h3>
        <ul>
          <li>Unregistered title.</li>
          <li>Missing or delayed documents, or missing building regulations / planning approvals.</li>
          <li>Multiple or private mortgages to redeem.</li>
          <li>Leasehold rather than freehold.</li>
          <li>We are not on your chosen lender&rsquo;s panel.</li>
          <li>Long chains or executor / probate sales.</li>
          <li>Property subject to a tenancy.</li>
          <li>Short residual lease term requiring extension.</li>
          <li>New-build purchases.</li>
        </ul>

        <h3>How long will my house purchase take?</h3>
        <p>The average process takes between <strong>8&ndash;12 weeks</strong> from offer acceptance to move-in, depending on the parties in the chain and factors above. First-time buyer new-build purchases and leasehold matters requiring lease extensions may take significantly longer.</p>
      </>
    ),
    contact: { name: 'Tiana Daley', role: 'Head of Residential Conveyancing', phone: '01582 466145', email: 't.daley@pmsolicitors.co.uk' },
  },
  {
    id: 'litigation',
    title: 'Litigation',
    lead: 'Debt recovery for business up to £100,000 and other civil litigation.',
    body: (
      <>
        <h3>Debt Recovery for Business</h3>
        <p>Please note we are talking here of debt recovery up to a value of <strong>&pound;100,000</strong> and not beyond, and undisputed. Our fee for a basic pre-action letter to recover a debt is <strong>&pound;175 plus VAT</strong>.</p>
        <p><em>If the alleged debtor challenges the claim we will consider the nature of the defence, take your full instructions and then provide a detailed written estimate of costs, reflecting the likely complexity of the matter. It is unlikely we will be able to give a fixed-fee proposal for defended work; a counterclaim is often likely once proceedings issue.</em></p>
        <p>After providing an initial estimate we will:</p>
        <ul>
          <li>Require you to formally instruct us in writing and pay the amount of the initial estimate upfront.</li>
          <li>Notify you if our costs on a recorded-time basis are likely to exceed, or have exceeded, the estimate and request further top-up monies to reflect progress and anticipated future time.</li>
          <li>Bill you monthly during the progress of the claim on a recorded-time basis.</li>
        </ul>

        <h3>Hourly rates for litigation work</h3>
        <ul>
          <li><strong>Partner</strong> &mdash; &pound;200 to &pound;330 per hour depending on the circumstances.</li>
          <li><strong>Associate</strong> &mdash; &pound;250 per hour.</li>
        </ul>

        <h3>Court fees (disbursements payable by you)</h3>
        <p>If the matter proceeds to court, civil court fees are a disbursement that you must pay, and we will require payment upfront before issuing proceedings. If Judgment is given in your favour, further fees apply for civil enforcement.</p>

        <h4 className="text-cream-50 font-serif text-lg mt-6">Money Claims &mdash; issue fees</h4>
        <p>Based on the amount claimed including interest. Round fractions of pence down to the nearest penny.</p>
        <table>
          <thead><tr><th>Value of your claim</th><th>Fee payable</th></tr></thead>
          <tbody>
            <tr><td>Up to &pound;300</td><td>&pound;35</td></tr>
            <tr><td>&pound;300.01 &ndash; &pound;500</td><td>&pound;50</td></tr>
            <tr><td>&pound;500.01 &ndash; &pound;1,000</td><td>&pound;70</td></tr>
            <tr><td>&pound;1,000.01 &ndash; &pound;1,500</td><td>&pound;80</td></tr>
            <tr><td>&pound;1,500.01 &ndash; &pound;3,000</td><td>&pound;115</td></tr>
            <tr><td>&pound;3,000.01 &ndash; &pound;5,000</td><td>&pound;205</td></tr>
            <tr><td>&pound;5,000.01 &ndash; &pound;10,000</td><td>&pound;455</td></tr>
            <tr><td>&pound;10,000.01 &ndash; &pound;200,000</td><td>5% of the value of the claim</td></tr>
            <tr><td>Above &pound;200,000</td><td>&pound;10,000</td></tr>
          </tbody>
        </table>

        <h4 className="text-cream-50 font-serif text-lg mt-6">Order to obtain information from a debtor</h4>
        <ul>
          <li>Application for an order for a debtor or other person to attend court to provide information &mdash; <strong>&pound;67</strong>.</li>
          <li>Request bailiff service of an order for a debtor to attend court for questioning &mdash; <strong>&pound;135</strong>.</li>
        </ul>

        <h4 className="text-cream-50 font-serif text-lg mt-6">Warrants</h4>
        <ul>
          <li>Warrant of control (recovery of a sum of money) &mdash; <strong>&pound;94</strong>.</li>
          <li>Warrant of delivery (for goods) &mdash; <strong>&pound;148</strong>.</li>
          <li>Warrant of possession (recovery of property or land) &mdash; <strong>&pound;148</strong>.</li>
          <li>Request a further attempt to execute a warrant at a new address (except where a warrant has been suspended) &mdash; <strong>&pound;37</strong>.</li>
        </ul>

        <h4 className="text-cream-50 font-serif text-lg mt-6">Writs (High Court only)</h4>
        <ul>
          <li>Sealing a writ of control (recovery of a sum of money) &mdash; <strong>&pound;80</strong>.</li>
          <li>Sealing a writ of delivery (for goods) &mdash; <strong>&pound;80</strong>.</li>
          <li>Sealing a writ of possession (recovery of property or land) &mdash; <strong>&pound;80</strong>.</li>
        </ul>
        <p className="text-sm text-cream-100/60">Where a warrant or writ of delivery or possession also includes a claim for money, there is no additional fee.</p>

        <h4 className="text-cream-50 font-serif text-lg mt-6">Attachment of Earnings</h4>
        <p>Application for an attachment of earnings order &mdash; <strong>&pound;135</strong> per defendant against whom the order is requested.</p>
        <p>On a consolidation attachment of earnings order, a fee of <strong>10p for every &pound;1</strong> (or part &pound;1) of money paid into court is deducted before payment out to creditors.</p>

        <h4 className="text-cream-50 font-serif text-lg mt-6">Enforcing an award</h4>
        <ul>
          <li>In the County Court &mdash; application for enforcement of an award for a sum of money or other decision made by any court, tribunal, body or person other than the High Court or County Court (unless exceptions apply) &mdash; <strong>&pound;54</strong>.</li>
          <li>In the High Court &mdash; request or application to register a judgment or order; for permission to enforce an arbitration award; or for a certificate or certified copy of a judgment or order for use abroad &mdash; <strong>&pound;80</strong>.</li>
        </ul>

        <h4 className="text-cream-50 font-serif text-lg mt-6">Other enforcement applications</h4>
        <ul>
          <li>Charging order (per order applied for) &mdash; <strong>&pound;135</strong>.</li>
          <li>Third party debt order (per party against whom the order is requested) &mdash; <strong>&pound;135</strong>.</li>
          <li>Judgment summons &mdash; <strong>&pound;135</strong>.</li>
          <li>Certified Enforcement Agent application (other non-money fee for County Courts) &mdash; <strong>&pound;377</strong>.</li>
        </ul>
      </>
    ),
    contact: { name: 'Simon Braun', role: 'Managing Partner', phone: '01582 466163', email: 's.braun@pmsolicitors.co.uk' },
  },
  {
    id: 'employment',
    title: 'Employment Law',
    lead: 'Advice on workplace disputes, settlement agreements and employment tribunal claims.',
    body: (
      <>
        <p>Our Employment Law team advises employees and employers on the full range of workplace matters. Fees are typically quoted after an initial discussion of your circumstances so that we can give you a realistic estimate.</p>
        <ul>
          <li><strong>Settlement agreements</strong> for employees &mdash; usually a fixed fee, contribution from the employer wherever possible.</li>
          <li><strong>Unfair or wrongful dismissal claims</strong> &mdash; hourly-rate work with a written estimate before you commit.</li>
          <li><strong>Discrimination, whistleblowing and grievance advice</strong> &mdash; scoped and estimated at the outset.</li>
          <li><strong>Employer advisory work</strong> &mdash; contracts, policies, disciplinary and redundancy support.</li>
        </ul>
        <p>For a full fee estimate specific to your matter, please contact us on <a href="tel:01582466140" className="text-sage-400 hover:underline">01582 466140</a> or via our <Link to="/contact" className="text-sage-400 hover:underline">contact page</Link>.</p>
      </>
    ),
    contact: { name: 'Enquiries', role: 'Employment Law', phone: '01582 466140', email: 'hello@hedgeslaw.co.uk' },
  },
];

export default function Pricing() {
  const [open, setOpen] = useState('conveyancing');

  return (
    <div className="pt-24">
      <section className="bg-forest-950 py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <p className="text-sage-400 tracking-[0.3em] text-xs uppercase mb-5 reveal">Our fees</p>
          <h1 className="font-serif text-cream-50 text-5xl md:text-7xl leading-tight reveal">Pricing</h1>
          <p className="mt-6 text-cream-100/85 text-xl md:text-2xl font-serif italic max-w-3xl reveal">We believe in transparent, straightforward fees.</p>
          <p className="mt-4 text-cream-100/70 text-base max-w-3xl reveal">Below is an overview of our indicative pricing for Residential Conveyancing, Litigation and Employment Law matters. Fees quoted are guidance only; we will always provide a written estimate specific to your circumstances before work begins.</p>
        </div>
      </section>

      <section className="bg-forest-900 py-16">
        <div className="max-w-5xl mx-auto px-6 md:px-10 space-y-4">
          {SECTIONS.map((s) => {
            const isOpen = open === s.id;
            return (
              <div key={s.id} className="bg-forest-950 border border-forest-800">
                <button onClick={() => setOpen(isOpen ? null : s.id)} className="w-full flex items-center justify-between p-6 md:p-8 text-left hover:bg-forest-900/40 transition-colors">
                  <div>
                    <h2 className="font-serif text-cream-50 text-2xl md:text-3xl">{s.title}</h2>
                    <p className="mt-2 text-cream-100/65 text-sm md:text-base">{s.lead}</p>
                  </div>
                  <ChevronDown className={`h-5 w-5 text-sage-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                {isOpen && (
                  <div className="border-t border-forest-800 p-6 md:p-10 pm-prose text-cream-100/85">
                    {s.body}
                    <div className="mt-8 pt-6 border-t border-forest-800">
                      <p className="text-xs uppercase tracking-[0.25em] text-cream-100/50">For more information</p>
                      <p className="mt-2 font-serif text-cream-50 text-lg">{s.contact.name} <span className="text-cream-100/60 text-sm not-italic">&mdash; {s.contact.role}</span></p>
                      <div className="mt-2 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                        <a href={`tel:${s.contact.phone.replace(/\s/g,'')}`} className="flex items-center gap-2 text-cream-100/80 hover:text-sage-400"><Phone className="h-4 w-4 text-sage-400" />{s.contact.phone}</a>
                        <a href={`mailto:${s.contact.email}`} className="flex items-center gap-2 text-cream-100/80 hover:text-sage-400"><Mail className="h-4 w-4 text-sage-400" />{s.contact.email}</a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section className="bg-forest-800 py-16">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <h3 className="font-serif text-cream-50 text-3xl md:text-4xl">Need a bespoke quote?</h3>
          <p className="mt-4 text-cream-100/75">Every matter is different. Contact us and we will provide a clear written estimate before any work begins.</p>
          <Link to="/contact" className="mt-8 inline-flex items-center gap-3 bg-sage-500 text-forest-900 px-8 py-4 text-sm font-medium tracking-wide hover:bg-sage-600 transition-colors">Contact Us <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </section>
    </div>
  );
}
